import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {ComboBox, Text} from '@forecast-it/design-system';
import Util from '../../../shared/util/util';
import {createToast} from '../../../shared/components/toasts/another-toast/toaster';
import UpdateXeroExportDefaultsMutation from '../../../../mutations/update_xero_export_defaults_mutation';
import {ExportDefaultsCard} from './ExportDefaultsCard';
import {
	StoredXeroBillFrom,
	XERO_EXPORT_DEFAULT_TOGGLES,
	XeroExportDefaultField,
	XeroExportDefaults,
	XeroOrganization,
	xeroExportDefaultsFor,
} from './xeroExportDefaults';

type Props = {
	organizations: ReadonlyArray<XeroOrganization>;
	billFroms: ReadonlyArray<StoredXeroBillFrom | null> | null | undefined;
};

type OrganizationItem = {id: string; name: string};

export const XeroExportDefaultsSection = ({organizations, billFroms}: Props) => {
	const {formatMessage} = useIntl();
	const [tenantId, setTenantId] = useState(organizations[0].tenantId);
	const [values, setValues] = useState<XeroExportDefaults>(() => xeroExportDefaultsFor(billFroms, organizations[0].tenantId));
	const [saving, setSaving] = useState(false);

	const selected = organizations.find(o => o.tenantId === tenantId) || organizations[0];
	const saved = xeroExportDefaultsFor(billFroms, selected.tenantId);

	// Switching organisation discards an unsaved edit (spec 027). After a save, Relay updates the
	// store and new billFroms arrive: show what the server stored.
	useEffect(() => {
		setValues(saved);
	}, [selected.tenantId, saved.projectInDescription]);

	const onToggle = (field: XeroExportDefaultField, checked: boolean) =>
		setValues(current => ({...current, [field]: checked}));

	const onSave = () => {
		setSaving(true);
		Util.CommitMutation(
			UpdateXeroExportDefaultsMutation,
			{tenantId: selected.tenantId, projectInDescription: values.projectInDescription},
			() => {
				setSaving(false);
				createToast({duration: 10000, message: formatMessage({id: 'common.settings_saved'})});
			},
			true,
			null,
			(transaction: unknown, onFailure: (transaction: unknown) => boolean | void) => {
				// onFailure returns true when it has scheduled a retry; keep the spinner in that case.
				if (onFailure(transaction) !== true) setSaving(false);
			}
		);
	};

	const items: OrganizationItem[] = organizations.map(o => ({id: o.tenantId, name: o.organization}));

	return (
		<ExportDefaultsCard
			titleId="integrations.xero.export_defaults.title"
			introId="integrations.xero.export_defaults.intro"
			saveId="common.save"
			toggles={XERO_EXPORT_DEFAULT_TOGGLES}
			values={values}
			onToggle={onToggle}
			onSave={onSave}
			saving={saving}
			cyPrefix="xero-export-defaults"
			beforeToggles={
				<>
					{organizations.length > 1 ? (
						<ComboBox
							label={formatMessage({id: 'common.organization'})}
							defaultItems={items}
							value={selected.tenantId}
							onChange={(key: string | null) => key && setTenantId(key)}
							disabled={saving}
							width={ComboBox.WIDTH.FLEX}
							data-cy="xero-export-defaults-organization"
						>
							{(item: OrganizationItem) => (
								<ComboBox.Item key={item.id} textValue={item.name}>
									<Text>{item.name}</Text>
								</ComboBox.Item>
							)}
						</ComboBox>
					) : null}
					<Text data-cy="xero-export-defaults-applies-to">
						{formatMessage(
							{id: 'integrations.xero.export_defaults.applies_to'},
							{organization: selected.organization}
						)}
					</Text>
				</>
			}
		/>
	);
};
